/**
 * Price-and-accept (`/leads/:leadId/accept/:requestId`) — answering a buyer's
 * request.
 *
 * This is a SCREEN, not a button, and the reason is structural:
 * `QuotationItem.unitPrice` is NOT NULL while a Selection sent at the default
 * `priceMode: NONE` has no prices at all, so there is nothing to copy. Where the
 * Selection did carry a price it prefills here, and accepting is one tap.
 *
 * The lines, the totals and the created-panel are shared with the booth path
 * (`$leadId.quote.tsx`) — see `components/quotation/`. This file owns only
 * where the lines come from and which endpoint answers.
 */
import {
  AcceptQuoteRequestInput,
  DEFAULT_QUOTATION_TERMS,
  formatMoney,
  NO_AMOUNT_LABEL,
  type CreatedQuotation
} from '@paradise/shared';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';

import { ConfirmDialog } from '@/components/confirm-dialog';
import { useLeadDetailQuery } from '@/components/lead-detail/use-lead-detail';
import { QuotationCreatedPanel } from '@/components/quotation/quotation-created-panel';
import { QuotationLineEditor } from '@/components/quotation/quotation-line-editor';
import {
  allLinesPriced,
  linesTotal,
  toQuotationItems,
  type QuotationLine
} from '@/components/quotation/quotation-lines';
import { liveQuotationRefByProduct, useLeadQuotations } from '@/components/quotation/use-lead-quotations';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import { apiRequest, describeApiFailure } from '@/lib/api/http';
import { useSessionStore } from '@/lib/auth/session';
import { useCatalogueProducts } from '@/lib/catalogue-data';

export const Route = createFileRoute('/_app/leads/$leadId/accept/$requestId')({
  component: PriceAndAcceptPage
});

function PriceAndAcceptPage() {
  const { leadId, requestId } = Route.useParams();
  const navigate = useNavigate();
  const detailQuery = useLeadDetailQuery(leadId);
  // Photo, live catalogue price and MOQ come from the offline mirror — the
  // request itself carries only a name and an itemNo.
  const catalogueProducts = useCatalogueProducts();
  /** Signs the share message — the buyer should know which of us sent the quotation. */
  const senderName = useSessionStore(state => state.user?.name ?? null);
  const productById = useMemo(() => new Map(catalogueProducts.map(p => [p.id, p] as const)), [catalogueProducts]);
  const [lines, setLines] = useState<QuotationLine[] | null>(null);
  // Prefilled, not fixed: the office edits the GST basis or the packing per
  // buyer, and what they send is frozen onto the row.
  const [terms, setTerms] = useState(DEFAULT_QUOTATION_TERMS);
  const [created, setCreated] = useState<CreatedQuotation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmCreate, setConfirmCreate] = useState(false);
  const [confirmDecline, setConfirmDecline] = useState(false);

  const request = detailQuery.data?.detail.quoteRequests.find(entry => entry.id === requestId);

  // Display only, and deliberately not a block: a repeat ask about goods
  // already quoted usually wants a REVISION of that quotation, not a second
  // live one at a different price. Staff decide; this just makes it visible.
  const quotationsQuery = useLeadQuotations(leadId);
  const quotedRefByProduct = useMemo(
    () => liveQuotationRefByProduct(quotationsQuery.data ?? []),
    [quotationsQuery.data]
  );

  // One-time prefill: once staff have edited a price, a background refetch must
  // not overwrite it.
  //
  // Three fallbacks deep, because the FIRST one is usually empty: a Selection
  // sent at the default `priceMode: NONE` carries no `unitPrice` at all, so
  // pricing a request meant typing every figure from memory with nothing on
  // screen to price against. The snapshot and the live catalogue price are both
  // better starting points than a blank box, and `priceSource` says which one
  // is showing so nobody mistakes a catalogue price for an agreed one.
  useEffect(() => {
    if (request === undefined || lines !== null || productById.size === 0) return;
    setLines(
      request.items.map(item => {
        const product = productById.get(item.productId);
        const prefill = item.selectionUnitPrice ?? item.selectionListPriceSnapshot ?? product?.listPrice ?? '';
        return {
          productId: item.productId,
          productName: item.productName,
          itemNo: item.itemNo,
          quantity: item.quantity,
          quantityDraft: null,
          requestedQuantity: item.quantity,
          listPriceSnapshot: item.selectionListPriceSnapshot,
          unitPriceText: prefill,
          noteText: item.note ?? '',
          requestedNote: item.note,
          priceSource:
            item.selectionUnitPrice !== null
              ? 'selection'
              : item.selectionListPriceSnapshot !== null
                ? 'snapshot'
                : product?.listPrice != null
                  ? 'catalogue'
                  : 'none'
        };
      })
    );
  }, [request, lines, productById]);

  function patchLine(index: number, patch: Partial<QuotationLine>): void {
    setLines(prev => (prev === null ? prev : prev.map((entry, i) => (i === index ? { ...entry, ...patch } : entry))));
  }

  if (detailQuery.isPending) {
    return (
      <div className="grid h-dvh place-items-center text-sm text-muted-foreground">
        <span className="flex items-center gap-2">
          <Spinner className="size-4" aria-hidden="true" /> Loading…
        </span>
      </div>
    );
  }

  if (request === undefined || lines === null) {
    return (
      <div className="grid h-dvh place-items-center p-6 text-center">
        <div className="flex flex-col items-center gap-3">
          <p className="text-sm text-muted-foreground">This quote request could not be found.</p>
          <Button render={<Link to="/leads/$leadId" params={{ leadId }} />} variant="outline" size="sm">
            ‹ Back to buyer
          </Button>
        </div>
      </div>
    );
  }

  const total = linesTotal(lines);
  const quotationCurrency = detailQuery.data?.detail.currency ?? 'INR';

  async function submit() {
    if (lines === null) return;
    setBusy(true);
    setError(null);
    const parsed = AcceptQuoteRequestInput.safeParse({
      items: toQuotationItems(lines),
      terms: terms.trim() === '' ? null : terms.trim()
    });
    if (!parsed.success) {
      setError('Every line needs a price before this can be sent.');
      setBusy(false);
      return;
    }
    const outcome = await apiRequest<CreatedQuotation>(`/api/quote-requests/${requestId}/accept`, {
      method: 'POST',
      body: parsed.data
    });
    setBusy(false);
    if (outcome.kind === 'ok') setCreated(outcome.data);
    else setError(describeApiFailure(outcome));
  }

  async function decline() {
    setBusy(true);
    setError(null);
    const outcome = await apiRequest<unknown>(`/api/quote-requests/${requestId}/reject`, { method: 'POST' });
    setBusy(false);
    if (outcome.kind === 'ok') await navigate({ to: '/leads/$leadId', params: { leadId } });
    else setError(describeApiFailure(outcome));
  }

  if (created) {
    return (
      <QuotationCreatedPanel
        created={created}
        leadId={leadId}
        senderName={senderName}
        recipientEmail={request.requesterEmail}
      />
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 p-4 pb-24">
      <header className="flex items-center gap-2.5">
        <Button render={<Link to="/leads/$leadId" params={{ leadId }} />} variant="ghost" size="sm" className="-ml-1.5">
          ‹ Back
        </Button>
        <div className="font-heading truncate text-lg font-semibold">Price this request</div>
      </header>

      <div className="rounded-lg border bg-card p-3 text-sm">
        <div className="font-medium">{request.requesterName}</div>
        <div className="text-xs text-muted-foreground">
          {request.requesterCompany} · {request.requesterEmail}
          {request.requesterPhone ? ` · ${request.requesterPhone}` : ''}
        </div>
        {request.message && <p className="mt-1.5 text-sm whitespace-pre-wrap">{request.message}</p>}
      </div>

      <div className="flex flex-col gap-2.5">
        {lines.map((line, index) => (
          <QuotationLineEditor
            key={line.productId}
            line={line}
            product={productById.get(line.productId)}
            askedByName={request.requesterName}
            alreadyQuotedRef={quotedRefByProduct.get(line.productId)}
            onChange={patch => patchLine(index, patch)}
          />
        ))}
      </div>

      <div className="flex items-baseline justify-between border-t pt-2.5">
        <span className="text-sm font-medium">Quotation total</span>
        <span className="font-heading text-lg font-semibold">
          {total === null ? NO_AMOUNT_LABEL : formatMoney(total, quotationCurrency)}
        </span>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="quotation-terms" className="text-xs font-normal text-muted-foreground">
          Terms printed under the total
        </Label>
        <Textarea
          id="quotation-terms"
          rows={2}
          value={terms}
          onChange={event => setTerms(event.target.value)}
          placeholder="Leave blank to print no terms"
        />
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex flex-wrap justify-end gap-2">
        {/* Confirmed, like every other irreversible action in this app: the API
            refuses a request that is no longer PENDING, so a mis-tap here beside
            "Create quotation" permanently kills the buyer's request with no way
            back to price it. */}
        <Button
          type="button"
          variant="ghost"
          className="text-destructive"
          disabled={busy}
          onClick={() => setConfirmDecline(true)}
        >
          Decline request
        </Button>
        {/* Confirmed for the same reason as Decline beside it: this answers the
            buyer's request for good and mints a link they can accept from. */}
        <Button type="button" disabled={busy || !allLinesPriced(lines)} onClick={() => setConfirmCreate(true)}>
          Create quotation
        </Button>
      </div>

      <ConfirmDialog
        open={confirmCreate}
        onOpenChange={setConfirmCreate}
        title="Create this quotation?"
        description={`${String(lines.length)} item${lines.length === 1 ? '' : 's'} totalling ${
          total === null ? NO_AMOUNT_LABEL : formatMoney(total, quotationCurrency)
        } for ${request.requesterCompany}. This answers their request and mints a link they can accept from.`}
        confirmLabel="Create quotation"
        onConfirm={() => {
          setConfirmCreate(false);
          void submit();
        }}
      />

      <ConfirmDialog
        open={confirmDecline}
        onOpenChange={setConfirmDecline}
        title="Decline this request?"
        description="The buyer's request is closed and cannot be priced afterwards. They are not notified automatically."
        confirmLabel="Decline request"
        destructive
        onConfirm={() => void decline()}
      />
    </div>
  );
}
